import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
  Image,
  Linking,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter, useFocusEffect } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import ImageViewer from '../../../components/ImageViewer';
import SubItemRenderer from '../../../components/SubItemRenderer';
import { ProcedureChecklist, ChecklistItem } from '@/types/ProcedureChecklist';
import { useAuth } from '@/contexts/AuthContext';
import { ProcedureChecklistService } from '@/services/procedureChecklistService';
import { OfflineProcedureChecklistService } from '@/services/offlineProcedureChecklistService';
import { useCrossPlatformAlert } from '@/components/CrossPlatformAlert';
import { UserService } from '@/services/userService';
import { useNetworkStatus } from '@/utils/useNetworkStatus';
import OfflineInfoBar from '@/components/OfflineInfoBar';
import { ImageCacheService } from '@/utils/imageCache';
import { useOfflineButtons } from '@/utils/useOfflineButtons';
import { useResponsiveLayout } from '@/utils/useResponsiveLayout';

export default function ProcedureDetailsScreen() {
  const [checklist, setChecklist] = useState<ProcedureChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [createdByName, setCreatedByName] = useState<string>('');
  const [updatedByName, setUpdatedByName] = useState<string>('');
  const [isFromCache, setIsFromCache] = useState(false);
  const [cachedImageUris, setCachedImageUris] = useState<Map<string, string>>(new Map());
  // Tracks which top-level items have all their sub-items force-expanded
  const [expandedItemIds, setExpandedItemIds] = useState<Set<string>>(new Set());
  const { id } = useLocalSearchParams<{ id: string }>();
  const { user } = useAuth();
  const { isConnected } = useNetworkStatus();
  const { isButtonDisabled, getDisabledStyle } = useOfflineButtons();
  const { t } = useTranslation('common');
  const router = useRouter();
  const crossPlatformAlert = useCrossPlatformAlert();
  const responsive = useResponsiveLayout();

  const fetchChecklist = useCallback(async () => {
    if (!user || !id) return;
    
    try {
      // Try to get procedure from offline service first
      const { procedure, isFromCache: fromCache } = await OfflineProcedureChecklistService.getProcedureChecklist(id, user.role);
      
      if (procedure) {
        setChecklist(procedure);
        setIsFromCache(fromCache);
        
        // Load cached images for all procedure items
        await loadCachedImages(procedure);
        
        // Fetch user names for audit trail (only if online and not from cache)
        if (!fromCache && isConnected) {
          if (procedure.createdBy) {
            const createdName = await UserService.getUserDisplayName(procedure.createdBy).catch(() => '');
            setCreatedByName(createdName);
          }
          if (procedure.updatedBy) {
            const updatedName = await UserService.getUserDisplayName(procedure.updatedBy).catch(() => '');
            setUpdatedByName(updatedName);
          }
        }
        
        // If we got cached data and we're online, try to get fresh data in background
        if (fromCache && isConnected) {
          try {
            const freshProcedure = await ProcedureChecklistService.getProcedureChecklist(id, user.role);
            if (freshProcedure) {
              setChecklist(freshProcedure);
              setIsFromCache(false);
              
              // Update cached images for fresh data
              await loadCachedImages(freshProcedure);
              
              // Update user names for fresh data
              if (freshProcedure.createdBy) {
                const createdName = await UserService.getUserDisplayName(freshProcedure.createdBy).catch(() => '');
                setCreatedByName(createdName);
              }
              if (freshProcedure.updatedBy) {
                const updatedName = await UserService.getUserDisplayName(freshProcedure.updatedBy).catch(() => '');
                setUpdatedByName(updatedName);
              }
              
              // Force refresh cache to ensure fresh data is available offline
              try {
                await OfflineProcedureChecklistService.forceRefreshProcedures(user.role);
                console.log('[ProcedureDetails] Cache refreshed successfully after getting fresh procedure data');
              } catch (cacheError) {
                console.warn('[ProcedureDetails] Failed to refresh cache after getting fresh data:', cacheError);
                // Don't fail the operation if cache refresh fails
              }
            }
          } catch (error) {
            console.log('Failed to fetch fresh procedure data, keeping cached data:', error);
          }
        }
      } else {
        // Procedure not found
        crossPlatformAlert.showAlert({
          title: t('procedures.error'), 
          message: t('procedures.failedToLoad'),
          buttons: [{ text: 'OK', onPress: () => router.back() }]
        });
      }
    } catch (error) {
      console.error('Error fetching procedure/checklist:', error);
      crossPlatformAlert.showAlert({
        title: t('procedures.error'), 
        message: t('procedures.failedToLoad'),
        buttons: [{ text: 'OK', onPress: () => router.back() }]
      });
    } finally {
      setLoading(false);
    }
  }, [user, id, isConnected, router, t, crossPlatformAlert]);

  // Load cached images for procedure items and their sub-items
  const loadCachedImages = useCallback(async (procedure: ProcedureChecklist) => {
    try {
      await ImageCacheService.initialize();
      
      const newCachedUris = new Map<string, string>();

      const recursiveCacheSubItemImages = async (subItems: typeof procedure.items[0]['subItems']) => {
        if (!subItems) return;
        await Promise.all(
          subItems.map(async (subItem) => {
            if (subItem.image) {
              try {
                const cachedUri = await ImageCacheService.getCachedImage(subItem.image);
                newCachedUris.set(subItem.image, cachedUri);
              } catch {
                newCachedUris.set(subItem.image, subItem.image);
              }
            }
            await recursiveCacheSubItemImages(subItem.subItems);
          })
        );
      };
      
      // Load cached images for all items that have images
      await Promise.all(
        procedure.items.map(async (item) => {
          if (item.image) {
            try {
              const cachedUri = await ImageCacheService.getCachedImage(item.image);
              newCachedUris.set(item.image, cachedUri);
            } catch (error) {
              console.error(`Error loading cached image for item ${item.id}:`, error);
              // Fallback to original URI
              newCachedUris.set(item.image, item.image);
            }
          }
          await recursiveCacheSubItemImages(item.subItems);
        })
      );
      
      setCachedImageUris(newCachedUris);
    } catch (error) {
      console.error('Error loading cached images:', error);
    }
  }, []);

  // Authentication check - redirect if not logged in
  useEffect(() => {
    if (!user) {
      router.replace('/');
      return;
    }
  }, [user, router]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  // Refresh when the screen comes into focus
  useFocusEffect(
    useCallback(() => {
      if (user && id) {
        fetchChecklist();
      }
    }, [fetchChecklist, user, id])
  );

  const handleEdit = () => {
    if (checklist && !isButtonDisabled()) {
      router.push(`/procedures/${checklist.id}/edit`);
    }
  };

  const handleDelete = async () => {
    if (!user || !checklist || isButtonDisabled()) return;

    crossPlatformAlert.showAlert({
      title: t('procedures.deleteTitle'),
      message: t('procedures.deleteConfirmation', { title: checklist.title }),
      buttons: [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('procedures.delete.button'),
          style: 'destructive',
          onPress: async () => {
            try {
              await ProcedureChecklistService.softDeleteProcedureChecklist(checklist.id, user.role, user.uid);
              
              // Force refresh cache to reflect deletion
              try {
                await OfflineProcedureChecklistService.forceRefreshProcedures(user.role);
                console.log('[ProcedureDetails] Cache refreshed successfully after procedure deletion');
              } catch (cacheError) {
                console.warn('[ProcedureDetails] Failed to refresh cache after procedure deletion:', cacheError);
                // Don't fail the operation if cache refresh fails
              }
              
              crossPlatformAlert.showAlert({ 
                title: t('common.success'), 
                message: t('procedures.deleteSuccess'),
                buttons: [
                  { text: 'OK', onPress: () => router.back() }
                ]
              });
            } catch (error) {
              console.error('Error deleting procedure/checklist:', error);
              crossPlatformAlert.showAlert({ title: t('common.error'), message: t('procedures.deleteError') });
            }
          },
        },
      ]
    });
  };

  const handleRestore = async () => {
    if (!user || !checklist || isButtonDisabled()) return;

    try {
      await ProcedureChecklistService.restoreProcedureChecklist(checklist.id, user.role, user.uid);
      
      // Force refresh cache to reflect restoration
      try {
        await OfflineProcedureChecklistService.forceRefreshProcedures(user.role);
        console.log('[ProcedureDetails] Cache refreshed successfully after procedure restoration');
      } catch (cacheError) {
        console.warn('[ProcedureDetails] Failed to refresh cache after procedure restoration:', cacheError);
        // Don't fail the operation if cache refresh fails
      }
      
      await fetchChecklist(); // Refresh the data
      crossPlatformAlert.showAlert({ title: t('common.success'), message: t('procedures.restoreSuccess') });
    } catch (error) {
      console.error('Error restoring procedure/checklist:', error);
      crossPlatformAlert.showAlert({ title: t('common.error'), message: t('procedures.restoreError') });
    }
  };

  const handleOpenLink = async (url: string) => {
    try {
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      } else {
        crossPlatformAlert.showAlert({ title: t('common.error'), message: t('contact.cannotOpenLink') });
      }
    } catch (error) {
      console.error('Error opening link:', error);
      crossPlatformAlert.showAlert({ title: t('common.error'), message: t('contact.failedToOpenLink') });
    }
  };

  const canModifyChecklists = user?.role === 'manager' || user?.role === 'admin';

  // Collect all images from sub-items recursively
  const collectSubItemImages = useCallback((subItems: ChecklistItem['subItems']): { uri: string }[] => {
    if (!subItems) return [];
    const images: { uri: string }[] = [];
    subItems.forEach(subItem => {
      if (subItem.image) images.push({ uri: subItem.image });
      images.push(...collectSubItemImages(subItem.subItems));
    });
    return images;
  }, []);

  // Get all images from checklist items (including sub-items) for the image viewer
  const getImages = useCallback(() => {
    if (!checklist) return [];
    
    const images: { uri: string }[] = [];
    checklist.items
      .sort((a, b) => a.number - b.number)
      .forEach(item => {
        if (item.image) images.push({ uri: item.image });
        images.push(...collectSubItemImages(item.subItems));
      });
    return images;
  }, [checklist, collectSubItemImages]);

  // Find the index of the selected image in the images array
  const getImageIndex = useCallback((selectedImage: string) => {
    const images = getImages();
    return images.findIndex(img => img.uri === selectedImage);
  }, [getImages]);

  // Handle opening image viewer
  const handleImagePress = useCallback((imageUri: string) => {
    const index = getImageIndex(imageUri);
    if (index >= 0) {
      setImageIndex(index);
      setImageViewerVisible(true);
    }
  }, [getImageIndex]);

  const renderChecklistItem = (item: ChecklistItem, _index: number) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const allExpanded = expandedItemIds.has(item.id);

    const toggleExpandAll = () => {
      setExpandedItemIds(prev => {
        const next = new Set(prev);
        if (next.has(item.id)) {
          next.delete(item.id);
        } else {
          next.add(item.id);
        }
        return next;
      });
    };

    return (
    <View key={item.id} style={styles.itemContainer}>
      <View style={styles.itemHeader}>
        <View style={styles.itemNumber}>
          <Text style={styles.itemNumberText}>{item.number}</Text>
        </View>
        <View style={styles.itemContent}>
          <Text style={styles.itemTopic}>{item.topic}</Text>
        </View>
        {/* Expand/collapse all sub-items button */}
        {hasSubItems && (
          <TouchableOpacity
            style={styles.expandAllButton}
            onPress={toggleExpandAll}
            accessibilityLabel={allExpanded ? t('procedures.subItems.collapseAll') : t('procedures.subItems.expandAll')}
          >
            <Ionicons
              name={allExpanded ? 'contract-outline' : 'expand-outline'}
              size={20}
              color="#0066CC"
            />
          </TouchableOpacity>
        )}
      </View>

      {item.image && (
        <TouchableOpacity 
          style={styles.itemImageContainer}
          onPress={() => handleImagePress(item.image!)}
          activeOpacity={0.9}
        >
          <Image 
            source={{ uri: cachedImageUris.get(item.image) || item.image }} 
            style={styles.itemImage} 
          />
        </TouchableOpacity>
      )}

      {/* Flat content (old-style or items without sub-items) */}
      {!item.subItems?.length && (
        <Text style={styles.itemContentText}>{item.content}</Text>
      )}

      {/* Nested sub-items */}
      {hasSubItems && (
        <View style={styles.subItemsContainer}>
          {item.subItems!.map((subItem) => (
            <SubItemRenderer
              key={subItem.id}
              item={subItem}
              depth={0}
              cachedImageUris={cachedImageUris}
              onImagePress={handleImagePress}
              forceExpanded={allExpanded}
            />
          ))}
        </View>
      )}

      {item.link && (
        <TouchableOpacity 
          style={styles.linkButton} 
          onPress={() => handleOpenLink(item.link!)}
        >
          <Ionicons name="link-outline" size={16} color="#0066CC" />
          <Text style={styles.linkButtonText}>{t('procedures.openLink')}</Text>
        </TouchableOpacity>
      )}

      {item.file && (
        <TouchableOpacity 
          style={styles.fileButton} 
          onPress={() => handleOpenLink(item.file!)}
        >
          <Ionicons name="document-outline" size={16} color="#4CAF50" />
          <Text style={styles.fileButtonText}>{t('procedures.openFile')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0066CC" />
        <Text style={styles.loadingText}>{t('procedures.loading')}</Text>
      </View>
    );
  }

  if (!checklist) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="alert-circle-outline" size={64} color="#F44336" />
        <Text style={styles.errorTitle}>{t('procedures.notFound')}</Text>
        <Text style={styles.errorDescription}>
          {t('procedures.notFoundDescription')}
        </Text>
        <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
          <Text style={styles.backButtonText}>{t('procedures.goBack')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Offline info bar */}
      <OfflineInfoBar 
        visible={!isConnected || isFromCache} 
        message={!isConnected ? t('offline.noConnection') : t('offline.viewingCachedData')}
      />
      
      {/* Single ScrollView containing all content */}
      <ScrollView 
        style={styles.scrollContainer} 
        contentContainerStyle={[
          styles.scrollContent,
          responsive.isDesktop && {
            paddingHorizontal: responsive.spacing.large,
            alignItems: 'center',
          }
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Content wrapper for max-width on desktop */}
        <View style={[
          responsive.isDesktop && {
            width: '100%',
            maxWidth: responsive.maxContentWidth,
          }
        ]}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerContent}>
              <Text style={[
                styles.title,
                { fontSize: responsive.fontSize.title }
              ]}>{checklist.title}</Text>
              {checklist.description ? (
                <Text style={[
                  styles.description,
                  { fontSize: responsive.fontSize.body }
                ]}>{checklist.description}</Text>
              ) : null}
              
              <View style={styles.metadata}>
                <Text style={styles.metadataText}>
                  {checklist.items.length} {checklist.items.length === 1 ? t('procedures.items') : t('procedures.itemsPlural')}
                </Text>
                {checklist.createdAt ? (
                  <Text style={styles.metadataText}>
                    {t('procedures.created')} {checklist.createdAt.toLocaleDateString()} {checklist.createdAt.toLocaleTimeString()}
                    {createdByName && ` ${t('procedures.by')} ${createdByName}`}
                  </Text>
                ) : null}
                {checklist.updatedAt ? (
                  <Text style={styles.metadataText}>
                    {t('procedures.updated')} {checklist.updatedAt.toLocaleDateString()} {checklist.updatedAt.toLocaleTimeString()}
                    {updatedByName && ` ${t('procedures.by')} ${updatedByName}`}
                  </Text>
                ) : null}
              </View>
            </View>

            {/* Status badge for deleted items (admin only) */}
            {checklist.isDeleted && user?.role === 'admin' && (
              <View style={styles.deletedBadge}>
                <Text style={styles.deletedBadgeText}>{t('procedures.deleted')}</Text>
              </View>
            )}
          </View>

          {/* Action buttons */}
          {!checklist.isDeleted && (
            <View style={styles.actionButtons}>
              <TouchableOpacity 
                style={[
                  styles.executeButton,
                  responsive.isDesktop && {
                    paddingHorizontal: 24,
                    paddingVertical: 12,
                  }
                ]} 
                onPress={() => router.push(`/procedures/${checklist.id}/execute`)}
              >
                <Ionicons name="play-outline" size={20} color="#fff" />
                <Text style={styles.executeButtonText}>
                  {t('procedures.execute.button')}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {canModifyChecklists && (
            <View style={styles.actionButtons}>
              {!checklist.isDeleted && (
                <>
                  <TouchableOpacity 
                    style={[
                      styles.editButton, 
                      getDisabledStyle(),
                      responsive.isDesktop && {
                        paddingHorizontal: 24,
                        paddingVertical: 12,
                      }
                    ]} 
                    onPress={handleEdit}
                    disabled={isButtonDisabled()}
                  >
                    <Ionicons name="create-outline" size={20} color={isButtonDisabled() ? "#999" : "#fff"} />
                    <Text style={[styles.editButtonText, isButtonDisabled() && { color: '#999' }]}>
                      {t('procedures.edit')}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity 
                    style={[
                      styles.deleteButton, 
                      getDisabledStyle(),
                      responsive.isDesktop && {
                        paddingHorizontal: 24,
                        paddingVertical: 12,
                      }
                    ]} 
                    onPress={handleDelete}
                    disabled={isButtonDisabled()}
                  >
                    <Ionicons name="trash-outline" size={20} color={isButtonDisabled() ? "#999" : "#fff"} />
                    <Text style={[styles.deleteButtonText, isButtonDisabled() && { color: '#999' }]}>
                      {t('procedures.delete.button')}
                    </Text>
                  </TouchableOpacity>
                </>
              )}

              {user?.role === 'admin' && checklist.isDeleted && (
                <TouchableOpacity 
                  style={[
                    styles.restoreButton, 
                    getDisabledStyle(),
                    responsive.isDesktop && {
                      paddingHorizontal: 24,
                      paddingVertical: 12,
                    }
                  ]} 
                  onPress={handleRestore}
                  disabled={isButtonDisabled()}
                >
                  <Ionicons name="refresh-outline" size={20} color={isButtonDisabled() ? "#999" : "#fff"} />
                <Text style={[styles.restoreButtonText, isButtonDisabled() && { color: '#999' }]}>
                  {t('procedures.restore.button')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Checklist items */}
        <View style={styles.content}>
          {checklist.items
            .sort((a, b) => a.number - b.number)
            .map((item, index) => renderChecklistItem(item, index))}
        </View>
        </View>
      </ScrollView>

      {/* Image Viewer Modal */}
      <ImageViewer
        images={getImages()}
        imageIndex={imageIndex}
        visible={imageViewerVisible}
        onRequestClose={() => setImageViewerVisible(false)}
        onImageIndexChange={setImageIndex}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#F44336',
    marginTop: 16,
    marginBottom: 8,
  },
  errorDescription: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
  },
  backButton: {
    backgroundColor: '#0066CC',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  backButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  header: {
    backgroundColor: '#fff',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  description: {
    color: '#666',
    marginBottom: 8,
    lineHeight: 22,
  },
  metadata: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  metadataText: {
    fontSize: 12,
    color: '#999',
    marginRight: 16,
  },
  deletedBadge: {
    backgroundColor: '#F44336',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  deletedBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  actionButtons: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  executeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FF9800',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    marginRight: 8,
  },
  executeButtonText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 4,
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#4CAF50',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    marginRight: 8,
  },
  editButtonText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 4,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F44336',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  deleteButtonText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 4,
  },
  restoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FF9800',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  restoreButtonText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 4,
  },
  content: {
    padding: 16,
  },
  itemContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  itemHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  itemNumber: {
    backgroundColor: '#0066CC',
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  itemNumberText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  itemContent: {
    flex: 1,
  },
  itemTopic: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  expandAllButton: {
    padding: 4,
    marginLeft: 8,
  },
  itemImageContainer: {
    marginBottom: 12,
  },
  itemImage: {
    width: '100%',
    height: 200,
    borderRadius: 8,
    resizeMode: 'cover',
  },
  itemContentText: {
    fontSize: 16,
    color: '#333',
    lineHeight: 24,
    marginBottom: 12,
  },
  subItemsContainer: {
    marginTop: 8,
    marginBottom: 8,
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    marginBottom: 4,
  },
  linkButtonText: {
    color: '#0066CC',
    fontSize: 16,
    marginLeft: 4,
    textDecorationLine: 'underline',
  },
  fileButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  fileButtonText: {
    color: '#4CAF50',
    fontSize: 16,
    marginLeft: 4,
    textDecorationLine: 'underline',
  },
});
